import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Grid,
  TextField,
  InputAdornment,
  Chip,
  Avatar,
  Typography,
} from '@mui/material';
import {
  StarHalf as EvaluationIcon,
  Search as SearchIcon,
  Star as StarIcon,
  Person as PersonIcon,
  Assessment as ScoreIcon,
} from '@mui/icons-material';
import PageHeader from '../../../components/PageHeader';
import ModernTable from '../../../components/ModernTable';
import StatsCard from '../../../components/StatsCard';
import api from '../../../api';
import { showError } from '../../../utils/sweetAlertConfig';

const PRIMARY = '#29204C';
const SUCCESS = '#68B977';

export default function Evaluation() {
  const [evaluations, setEvaluations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [stats, setStats] = useState({
    total: 0,
    excellent: 0,
    good: 0,
    average: 0,
  });

  useEffect(() => {
    loadEvaluations();
  }, []);

  const loadEvaluations = async () => {
    try {
      setLoading(true);
      // Replace with your actual API endpoint
      // const response = await api.get('/evaluations');
      // setEvaluations(response.data.evaluations || []);
      // setStats(response.data.stats || stats);

      // Mock data for demonstration
      setTimeout(() => {
        setEvaluations([]);
        setStats({
          total: 0,
          excellent: 0,
          good: 0,
          average: 0,
        });
        setLoading(false);
      }, 1000);
    } catch (error) {
      console.error('Error loading evaluations:', error);
      showError('Error', 'Failed to load evaluation data');
      setLoading(false);
    }
  };

  const getRatingChip = (rating) => {
    if (rating >= 9) {
      return (
        <Chip
          label="Excellent"
          size="small"
          sx={{
            bgcolor: `${SUCCESS}15`,
            color: SUCCESS,
            fontWeight: 600,
          }}
        />
      );
    } else if (rating >= 7) {
      return (
        <Chip
          label="Very Good"
          size="small"
          sx={{
            bgcolor: '#1976d215',
            color: '#1976d2',
            fontWeight: 600,
          }}
        />
      );
    } else if (rating >= 5) {
      return (
        <Chip
          label="Good"
          size="small"
          sx={{
            bgcolor: '#ed6c0215',
            color: '#ed6c02',
            fontWeight: 600,
          }}
        />
      );
    } else {
      return (
        <Chip
          label="Fair"
          size="small"
          sx={{
            bgcolor: '#d32f2f15',
            color: '#d32f2f',
            fontWeight: 600,
          }}
        />
      );
    }
  };

  const columns = [
    {
      field: 'candidate_name',
      label: 'Candidate',
      minWidth: 200,
      render: (row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ bgcolor: PRIMARY, width: 36, height: 36 }}>
            <PersonIcon fontSize="small" />
          </Avatar>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, color: PRIMARY }}>
              {row.candidate_name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {row.candidate_id}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: 'job_title',
      label: 'Job Position',
      minWidth: 180,
    },
    {
      field: 'evaluator',
      label: 'Evaluator',
      minWidth: 150,
    },
    {
      field: 'rating',
      label: 'Rating',
      width: 120,
      align: 'center',
      render: (row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 700,
              color: row.rating >= 7 ? SUCCESS : row.rating >= 5 ? '#ed6c02' : '#d32f2f',
            }}
          >
            {row.rating}/10
          </Typography>
        </Box>
      ),
    },
    {
      field: 'performance',
      label: 'Performance',
      width: 140,
      align: 'center',
      render: (row) => getRatingChip(row.rating),
    },
    {
      field: 'evaluation_date',
      label: 'Evaluation Date',
      width: 150,
      render: (row) =>
        row.evaluation_date
          ? new Date(row.evaluation_date).toLocaleDateString()
          : 'N/A',
    },
  ];

  const filteredEvaluations = evaluations.filter(
    (evaluation) =>
      evaluation.candidate_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      evaluation.candidate_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      evaluation.job_title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      evaluation.evaluator?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const paginatedEvaluations = filteredEvaluations.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <PageHeader
        title="Evaluation"
        subtitle="View and manage candidate evaluation records"
        icon={EvaluationIcon}
        breadcrumbs={[
          { label: 'Dashboard', link: '/dashboard' },
          { label: 'Evaluation' },
        ]}
      />

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Total Evaluations"
            value={stats.total}
            icon={EvaluationIcon}
            color={PRIMARY}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Excellent (9-10)"
            value={stats.excellent}
            icon={StarIcon}
            color={SUCCESS}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Good (7-8)"
            value={stats.good}
            icon={ScoreIcon}
            color="#1976d2"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Average (5-6)"
            value={stats.average}
            icon={ScoreIcon}
            color="#ed6c02"
          />
        </Grid>
      </Grid>

      {/* Search Bar */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search by candidate name, ID, job position, or evaluator..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              bgcolor: 'white',
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: PRIMARY,
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: PRIMARY,
              },
            },
          }}
        />
      </Box>

      {/* Evaluations Table */}
      <ModernTable
        columns={columns}
        data={paginatedEvaluations}
        loading={loading}
        page={page}
        rowsPerPage={rowsPerPage}
        totalRows={filteredEvaluations.length}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
        emptyMessage="No evaluation records found. Evaluations will appear here."
        showActions={false}
      />
    </Container>
  );
}
